import { useState } from 'react';
import {
  BulkDeleteDialog,
  DeleteDialog,
  ExcelImportDialog,
  ExcelImportErrorDialog,
  ConfirmationDialog,
  ErpDataTableV2
} from '@/components/custom/erp';
import { QuyDoiDonViTinhChiTietProvider, useQuyDoiDonViTinhChiTietContext } from './context';
import { QuyDoiDonViTinhChiTiet, QuyDoiDonViTinhChiTietInput } from '@/types/schemas';
import { useCRUD, useExcel, useExcelProcessor, useForms } from '@/hooks';
import { ExcelConfig, type ImportBody } from './utils/import-excel';
import { ColumnMapping, ValidationError } from '@/lib/excel';
import { getDataTableColumns } from './cols-definition';
import { ActionBar, FormDialog } from './components';
import { useAuth } from '@/contexts/auth-context';
import { toast } from '@/hooks/use-toast';
import { QUERY_KEYS } from '@/constants';
import { useSearchTable } from './hooks';

const PAGE_SIZE = 100;

function QuyDoiDonViTinhChiTietPage() {
  const [showErrorDialog, setShowErrorDialog] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [importErrorData, setImportErrorData] = useState<{
    errors: ValidationError[];
    columnMapping: Record<string, ColumnMapping>;
  } | null>(null);
  const [showImportErrorDialog, setShowImportErrorDialog] = useState(false);

  const {
    data: quyDoiDonViTinhChiTiets,
    totalItems,
    currentPage,
    detail,
    fetchDetail,
    handlePageChange,
    addItem: addQuyDoiDonViTinhChiTiet,
    updateItem: updateQuyDoiDonViTinhChiTiet,
    deleteItem: deleteQuyDoiDonViTinhChiTiet,
    refreshData: refreshQuyDoiDonViTinhChiTiets,
    searchData
  } = useCRUD<QuyDoiDonViTinhChiTiet, QuyDoiDonViTinhChiTietInput>({
    endpoint: QUERY_KEYS.QUY_DOI_DON_VI_TINH_CHI_TIET,
    pageSize: PAGE_SIZE
  });

  const {
    showForm,
    showDelete,
    formMode,
    selectedObj,
    selectedRowIndex,
    isCopyMode,
    isImportDialogOpen,
    setIsImportDialogOpen,

    handleCloseForm,
    handleCloseDelete,
    handleAddClick,
    handleEditClick,
    handleViewClick,
    handleDeleteClick,
    handleCopyClick,
    handleRowClick,
    showBulkDelete,
    handleCloseBulkDelete,
    clearSelection,
    handleOpenImportExcel
  } = useForms<QuyDoiDonViTinhChiTiet>(fetchDetail);

  const { searchState, handleSearchChange, onPageChange, hasFilterParams } = useSearchTable({
    searchData,
    isFiltered: false,
    handlePageChange: handlePageChange
  });
  const { isExporting } = useQuyDoiDonViTinhChiTietContext();
  const { entityUnit } = useAuth();
  const { processFile } = useExcelProcessor(ExcelConfig);
  const { getUUIDByCode } = useExcel();

  const displayData = hasFilterParams ? searchState.searchResults : quyDoiDonViTinhChiTiets || [];
  const resolvedCurrentPage = hasFilterParams ? searchState.currentPage : currentPage;
  const resolvedTotalItems = hasFilterParams ? searchState.totalCount : totalItems;

  const tables = [
    {
      name: '',
      rows: displayData,
      columns: getDataTableColumns(handleViewClick)
    }
  ];

  const handleImportBodies = async (bodies: ImportBody[]) => {
    const errors: Array<{ index: number; message: string }> = [];
    for (let index = 0; index < bodies.length; index++) {
      const body = bodies[index];
      try {
        await addQuyDoiDonViTinhChiTiet(body);
      } catch (error) {
        console.error('Error importing conversion row:', error);
        errors.push({ index: index + 1, message: 'Không thể nhập dòng này' });
      }
    }

    if (errors.length > 0) {
      toast({
        description: errors.map(err => `Dòng ${err.index}: ${err.message}`).join('\n'),
        variant: 'destructive'
      });
    } else {
      toast({
        description: `Đã nhập ${bodies.length} quy đổi ĐVT từ Excel`,
        variant: 'default'
      });
    }

    await refreshQuyDoiDonViTinhChiTiets();
  };

  const handleImportFromExcel = async (data: { errors?: string; file: File | null; sheetIndex?: string }) => {
    if (!entityUnit?.uuid) {
      toast({
        description: 'Không tìm thấy thông tin đơn vị',
        variant: 'destructive'
      });
      return;
    }

    if (!data.file) {
      toast({
        description: 'Vui lòng chọn file Excel',
        variant: 'destructive'
      });
      return;
    }

    const maxErrors = parseInt(data.errors || '200', 10);
    const sheetIndex = parseInt(data.sheetIndex || '0', 10);

    const result = await processFile({
      file: data.file,
      sheetIndex,
      maxErrors,
      getUUIDByCode,
      context: { entityUnitUUID: entityUnit.uuid }
    });

    if (result.success && result.data) {
      await handleImportBodies(result.data);
      setIsImportDialogOpen(false);
    } else if (result.errors && result.columnMapping) {
      setImportErrorData({ errors: result.errors, columnMapping: result.columnMapping });
      setShowImportErrorDialog(true);
      setIsImportDialogOpen(false);
    }
  };

  const handleFormSubmit = async (data: QuyDoiDonViTinhChiTietInput) => {
    try {
      if (formMode === 'add') {
        await addQuyDoiDonViTinhChiTiet(data);
      } else if (formMode === 'edit' && selectedObj) {
        await updateQuyDoiDonViTinhChiTiet(selectedObj.uuid, data);
      }
      refreshQuyDoiDonViTinhChiTiets();
      handleCloseForm();
      clearSelection();
    } catch (error: unknown) {
      console.error('Error submitting form:', error);

      const apiError = error as { response?: { data?: { detail?: string } } };
      if (apiError.response?.data?.detail?.includes('already exists')) {
        if (isCopyMode && formMode === 'add') {
          setErrorMessage('Thông tin quy đổi đã tồn tại');
        } else {
          setErrorMessage('Mã vật tư đã tồn tại trong nhóm mã');
        }
        setShowErrorDialog(true);
      }
    }
  };

  return (
    <div className='flex h-full min-h-[calc(100vh-120px)] w-full flex-col'>
      <ActionBar
        onAddIconClick={handleAddClick}
        onEditIconClick={() => selectedObj && handleEditClick()}
        onDeleteIconClick={() => selectedObj && handleDeleteClick()}
        onCopyIconClick={() => selectedObj && handleCopyClick()}
        onWatchIconClick={() => selectedObj && handleViewClick()}
        onRefreshClick={refreshQuyDoiDonViTinhChiTiets}
        onImportExcel={handleOpenImportExcel}
        hasRowSelected={Boolean(selectedObj)}
      />
      <div data-excel-operation-overlay-root className='min-h-0 flex-1 overflow-hidden'>
        <ErpDataTableV2
          tables={tables}
          onRowClick={handleRowClick}
          selectedRowId={selectedRowIndex || undefined}
          totalItems={resolvedTotalItems}
          currentPage={resolvedCurrentPage}
          onPageChange={onPageChange}
          pageSize={PAGE_SIZE}
          onSearchChange={handleSearchChange}
        />
      </div>

      {showForm && (
        <FormDialog
          mode={formMode}
          open={showForm}
          onClose={handleCloseForm}
          onSubmit={handleFormSubmit}
          onAddButtonClick={handleAddClick}
          onEditButtonClick={() => selectedObj && handleEditClick()}
          onCopyButtonClick={() => selectedObj && handleCopyClick()}
          onWatchButtonClick={() => selectedObj && handleViewClick()}
          onDeleteButtonClick={() => selectedObj && handleDeleteClick()}
          initialData={formMode === 'add' && !isCopyMode ? undefined : detail}
        />
      )}

      {showDelete && (
        <DeleteDialog
          open={showDelete}
          onClose={handleCloseDelete}
          selectedObj={selectedObj}
          deleteObj={deleteQuyDoiDonViTinhChiTiet}
          clearSelection={clearSelection}
        />
      )}

      {/* Dialog thông báo lỗi */}
      <ConfirmationDialog
        open={showErrorDialog}
        onClose={() => setShowErrorDialog(false)}
        onConfirm={() => setShowErrorDialog(false)}
        title='Thông báo'
        message={errorMessage}
        iconCode={260}
      />

      {isImportDialogOpen && (
        <ExcelImportDialog
          open={isImportDialogOpen}
          onClose={() => setIsImportDialogOpen(false)}
          onSubmit={handleImportFromExcel}
          title='Nhập dữ liệu từ Excel'
        />
      )}

      {showImportErrorDialog && importErrorData && (
        <ExcelImportErrorDialog
          open={showImportErrorDialog}
          onClose={() => {
            setShowImportErrorDialog(false);
            setImportErrorData(null);
          }}
          errors={importErrorData.errors}
          columnMapping={importErrorData.columnMapping}
        />
      )}

      {showBulkDelete && (
        <BulkDeleteDialog
          title='Danh mục quy đổi đơn vị tính'
          onClose={handleCloseBulkDelete}
          columns={getDataTableColumns(handleViewClick)}
          dataRows={displayData}
          handleDelete={async (uuid: string) => {
            await deleteQuyDoiDonViTinhChiTiet(uuid);
          }}
          onComplete={refreshQuyDoiDonViTinhChiTiets}
        />
      )}
    </div>
  );
}

export default function QuyDoiDonViTinhChiTietContent() {
  return (
    <QuyDoiDonViTinhChiTietProvider>
      <QuyDoiDonViTinhChiTietPage />
    </QuyDoiDonViTinhChiTietProvider>
  );
}
